import React, { useState, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import HeaderTwo from '../common/header/HeaderTwo';
import FooterOne from '../common/footer/FooterOne';
import { FilterSidebar, ListingCard, MapView } from '../components/directory';
import { listings, getListingsByType } from '../data';
import { Grid3X3, List, Map, ChevronLeft, ArrowRight, Wheat, Store, Package, Cherry, Carrot, Sprout, Leaf, Search } from 'lucide-react';

// Category metadata for display (slugs match CategoryGrid.js)
const categoryMeta = {
  farms: {
    title: 'Local Farms',
    description: 'Discover local farms growing fresh produce, raising livestock, and providing farm-fresh goods.',
    Icon: Wheat,
  },
  'farmers-markets': {
    title: 'Farmers Markets',
    description: 'Find weekly and seasonal farmers markets featuring local vendors and fresh products.',
    Icon: Store,
  },
  'csa-programs': {
    title: 'CSA Programs',
    description: 'Join Community Supported Agriculture programs for weekly shares of fresh, local produce.',
    Icon: Package,
  },
  'u-pick': {
    title: 'U-Pick Farms',
    description: 'Pick your own fruits and vegetables at these family-friendly farms.',
    Icon: Cherry,
  },
  'farm-stands': {
    title: 'Farm Stands',
    description: 'Roadside farm stands and farm stores offering fresh, local products.',
    Icon: Carrot,
  },
  'organic-producers': {
    title: 'Organic Producers',
    description: 'Certified organic farms and producers committed to sustainable practices.',
    Icon: Sprout,
  },
};

const Categories = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [showMap, setShowMap] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  // Get category info or default
  const category = categoryMeta[slug] || {
    title: slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'All Listings',
    description: 'Browse local food sources in this category.',
    Icon: Leaf,
  };

  // Map slug to listing type (types match mockData.js)
  const typeMap = {
    farms: 'farm',
    'farmers-markets': 'market',
    'csa-programs': 'csa',
    'u-pick': 'upick',
    'farm-stands': 'farmstand',
    'organic-producers': 'organic',
  };

  const listingType = typeMap[slug] || slug;

  // Filter listings by category
  const filteredListings = useMemo(() => {
    let result = slug ? getListingsByType(listingType) : listings;

    // Sort listings
    switch (sortBy) {
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        result = [...result].sort((a, b) => b.reviewCount - a.reviewCount);
        break;
      case 'name':
        result = [...result].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'featured':
      default:
        result = [...result].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    return result;
  }, [slug, listingType, sortBy]);

  return (
    <>
      <HeaderTwo />

      {/* Category Header */}
      <section style={styles.categoryHeader}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <Link to="/browse" style={styles.backLink}>
                <ChevronLeft size={18} />
                Back to All Listings
              </Link>
              <div style={styles.categoryTitle}>
                <span style={styles.categoryIcon}>
                  <category.Icon size={48} />
                </span>
                <div>
                  <h1 style={styles.title}>{category.title}</h1>
                  <p style={styles.description}>{category.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Listings Section */}
      <section style={styles.listingsSection}>
        <div className="container">
          {/* Controls Bar */}
          <div style={styles.controlsBar}>
            <div style={styles.resultsCount}>
              <span style={styles.count}>{filteredListings.length}</span> listings found
            </div>

            <div style={styles.controlsRight}>
              {/* Sort Dropdown */}
              <select
                style={styles.sortSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="featured">Featured First</option>
                <option value="rating">Highest Rated</option>
                <option value="reviews">Most Reviews</option>
                <option value="name">Name A-Z</option>
              </select>

              {/* View Toggle */}
              <div style={styles.viewToggle}>
                <button
                  style={{
                    ...styles.viewBtn,
                    ...(viewMode === 'grid' && !showMap ? styles.viewBtnActive : {}),
                  }}
                  onClick={() => { setViewMode('grid'); setShowMap(false); }}
                >
                  <Grid3X3 size={18} />
                </button>
                <button
                  style={{
                    ...styles.viewBtn,
                    ...(viewMode === 'list' && !showMap ? styles.viewBtnActive : {}),
                  }}
                  onClick={() => { setViewMode('list'); setShowMap(false); }}
                >
                  <List size={18} />
                </button>
                <button
                  style={{
                    ...styles.viewBtn,
                    ...(showMap ? styles.viewBtnActive : {}),
                  }}
                  onClick={() => setShowMap(!showMap)}
                >
                  <Map size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Map View */}
          {showMap && (
            <div style={styles.mapContainer}>
              <MapView listings={filteredListings} />
            </div>
          )}

          {/* Listings Grid/List */}
          {!showMap && (
            <div className="row">
              {filteredListings.length > 0 ? (
                filteredListings.map((listing, index) => (
                  <div
                    className={viewMode === 'grid' ? 'col-lg-4 col-md-6 col-12 mb-4' : 'col-12 mb-3'}
                    key={listing.id}
                  >
                    <div className="wow fadeInUp" data-wow-delay={`${index * 50}ms`}>
                      <ListingCard listing={listing} variant={viewMode} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <div style={styles.noResults}>
                    <span style={styles.noResultsIcon}><Search size={48} /></span>
                    <h3 style={styles.noResultsTitle}>No listings found</h3>
                    <p style={styles.noResultsText}>
                      There are no listings in this category yet. Check back soon or browse all listings.
                    </p>
                    <Link to="/browse" style={styles.browseAllBtn}>
                      Browse All Listings
                      <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Related Categories */}
          <div style={styles.relatedSection}>
            <h3 style={styles.relatedTitle}>Explore Other Categories</h3>
            <div style={styles.relatedCategories}>
              {Object.entries(categoryMeta)
                .filter(([key]) => key !== slug)
                .slice(0, 4)
                .map(([key, cat]) => (
                  <Link key={key} to={`/category/${key}`} style={styles.relatedCard}>
                    <span style={styles.relatedIcon}><cat.Icon size={32} /></span>
                    <span style={styles.relatedName}>{cat.title}</span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      <FooterOne />
    </>
  );
};

const styles = {
  categoryHeader: {
    padding: '40px 0 30px',
    backgroundColor: 'var(--thm-bg-sage)',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    color: 'var(--thm-base)',
    fontFamily: 'var(--thm-font-2)',
    fontWeight: '600',
    textDecoration: 'none',
    marginBottom: '20px',
  },
  categoryTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  categoryIcon: {
    fontSize: '48px',
    lineHeight: 1,
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    margin: '0 0 8px',
  },
  description: {
    fontSize: '16px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    margin: 0,
    maxWidth: '600px',
  },
  listingsSection: {
    padding: '40px 0 80px',
    backgroundColor: 'var(--thm-bg-cream)',
    minHeight: '60vh',
  },
  controlsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  resultsCount: {
    fontSize: '16px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
  },
  count: {
    fontWeight: '700',
    color: 'var(--thm-black)',
  },
  controlsRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  sortSelect: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: '#fff',
    fontSize: '14px',
    fontFamily: 'var(--thm-font-2)',
    color: 'var(--thm-black)',
    cursor: 'pointer',
    outline: 'none',
  },
  viewToggle: {
    display: 'flex',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  viewBtn: {
    padding: '10px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--thm-gray)',
    transition: 'all 0.2s ease',
  },
  viewBtnActive: {
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
  },
  mapContainer: {
    height: '500px',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '30px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  noResults: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  noResultsIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  noResultsTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '12px',
  },
  noResultsText: {
    fontSize: '16px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    marginBottom: '24px',
  },
  browseAllBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '14px 28px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
  },
  relatedSection: {
    marginTop: '60px',
    paddingTop: '40px',
    borderTop: '1px solid rgba(0,0,0,0.1)',
  },
  relatedTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '24px',
    textAlign: 'center',
  },
  relatedCategories: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  relatedCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '20px 30px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },
  relatedIcon: {
    fontSize: '32px',
  },
  relatedName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font-2)',
  },
};

export default Categories;
